import React, { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import SubCategories from "src/img/bodyAndMind/subcategories.svg";
import {
    Button,
    LottieLoadingIndicator,
    StandardModalActionGroup,
    StandardModal,
    StandardModalInfoCard,
    Toast
} from "src/components";
import { ManageSubCategoriesTable } from "./ManageSubCategoriesTable";
import { SubcategoryEditor } from "./SubcategoryEditor";
import { DirectoryTypeChooser } from "./DirectoryTypeChooser";
import { BodyAndMindContext } from "../../BodyAndMindContext";
import { reOrderSubcategory } from "../../api/subCategoryApi";
import { LoadingIndicatorButton } from "src/scenes/Survey/components/Utils";

const HeaderMetricCard = styled(StandardModalInfoCard)`
    width: auto;
    min-height: 6.25rem;
    align-content: center;
    border-color: #9FB0D0;
    box-shadow: 0 3px 10px rgba(29, 42, 89, 0.12);
`;

const HeaderMetricTitle = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.85rem;
    font-weight: 800;
    color: #171A22;
`;

const HeaderMetricCount = styled.div`
    font-size: 1.8rem;
    font-weight: 800;
    color: #171A22;
`;

function ManageSubCategories() {
    const {
        categoriesList,
        contentsList,
        subcategoriesList,
        toggleManageSubcategory,
        showAddSubcategory,
        toggleAddSubcategory,
        showEditSubcategory,
        toggleEditSubcategory
    } = useContext(BodyAndMindContext);

    const [isLoading, setIsLoading] = useState(false);
    const [payload, setPayload] = useState([]);
    const [companyCategoriesList, setCompanyCategoriesList] = useState([]);
    const [showDirectoryTypeChooser, setShowDirectoryTypeChooser] = useState(false);
    const [directoryLevelToCreate, setDirectoryLevelToCreate] = useState(null);

    const [subcategoryToUpdate, setSubcategoryToUpdate] = useState({});

    const openCreateChooser = () => setShowDirectoryTypeChooser(true);

    const onDirectoryTypeSelect = level => {
        setDirectoryLevelToCreate(level);
        setShowDirectoryTypeChooser(false);
        toggleAddSubcategory();
    };

    const closeAddSubcategory = () => {
        setDirectoryLevelToCreate(null);
        toggleAddSubcategory();
    };

    const reOrderFunction = async () => {
        setIsLoading(true);
        try {
            const response = await reOrderSubcategory({ "categories": payload });
            if (response) {
                setIsLoading(false);
                Toast.success("Categories reordered successfully");
                toggleManageSubcategory();
            }
        } catch (error) {
            setIsLoading(false);
            console.log("Error:", error);
        }
    };

    // Create a new list of categories for company-specific entries only
    useEffect(() => {
        const newCategoriesList = categoriesList.filter(item => item.companyUuid !== null);
        setCompanyCategoriesList(newCategoriesList);
    }, [categoriesList]);

    return (
        <>
            <StandardModal
                open={true}
                onClose={toggleManageSubcategory}
                title="Manage Subcategories, Topics & Subtopics"
                description="Create, edit, or delete sub-categories, topics, and sub-topics in your main categories to organise your content and help users find what they need faster."
                widthMode="table"
                headerAside={(
                    <HeaderMetricCard>
                        <HeaderMetricTitle>
                            <img src={SubCategories} width="24px" height="24px"/>
                            Sub-Categories, Topics & Sub-Topics
                        </HeaderMetricTitle>
                        <HeaderMetricCount>{subcategoriesList?.length || 0}</HeaderMetricCount>
                    </HeaderMetricCard>
                )}
                footer={(
                    <StandardModalActionGroup $pushRight>
                        <Button
                            onClick={() => toggleManageSubcategory()}
                            border="1px solid #000"
                            color="#000"
                            width="140px"
                            height="38px"
                            style={{ borderRadius: "7px", boxShadow: "none" }}
                        >
                            Close
                        </Button>
                        {isLoading ?
                            <LoadingIndicatorButton>
                                <LottieLoadingIndicator height="2rem" width="2rem" margin="5px" containerHeight="2rem" />
                            </LoadingIndicatorButton>
                            :
                            <Button
                                onClick={Object.keys(payload).length === 0 ? () => toggleManageSubcategory() : () => reOrderFunction()}
                                backgroundColor="#171A22"
                                border="1px solid #171A22"
                                color="#fff"
                                width="140px"
                                height="38px"
                                style={{ borderRadius: "7px", boxShadow: "none" }}
                            >
                                Save
                            </Button>}
                    </StandardModalActionGroup>
                )}
            >
                <ManageSubCategoriesTable
                    data={subcategoriesList || []}
                    contents={contentsList || []}
                    setPayload={setPayload}
                    setSubcategoryToUpdate={setSubcategoryToUpdate}
                    companyCategoriesList={companyCategoriesList}
                    onCreateClick={openCreateChooser}
                />
            </StandardModal>

            {/* MODALS */}
            {showDirectoryTypeChooser && (
                <DirectoryTypeChooser
                    open={showDirectoryTypeChooser}
                    onClose={() => setShowDirectoryTypeChooser(false)}
                    onSelect={onDirectoryTypeSelect}
                />
            )}

            {showAddSubcategory && (
                <SubcategoryEditor
                    open={showAddSubcategory}
                    onClose={closeAddSubcategory}
                    categories={companyCategoriesList}
                    directories={subcategoriesList || []}
                    initialDirectoryLevel={directoryLevelToCreate}
                />
            )}

            {showEditSubcategory && (
                <SubcategoryEditor open={showEditSubcategory} onClose={toggleEditSubcategory} categories={companyCategoriesList} directories={subcategoriesList || []} subcategory={subcategoryToUpdate} />
            )}
        </>
    );
}

export default ManageSubCategories;
